"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.processPaymentRequestUpdate = void 0;
const sendEmail_1 = require("../sendEmail");
const orders_1 = require("../orders");
const helpers_1 = require("../../helpers");
const generateReceipt_1 = require("./generateReceipt");
// Helper function to get active order lines
const getActiveOrderLines = (order) => {
    const variant = order.variants?.find((v) => v.id === order.default_variant_id);
    return (variant?.order_lines?.filter((ol) => ![
        helpers_1.OrderLineStatus.CANCELLED,
        helpers_1.OrderLineStatus.CANCELLED_REJECTED_BY_CLIENT,
        helpers_1.OrderLineStatus.CANCELLED_UNAVAILABLE,
    ].includes(ol.status)) || []);
};
// Helper function to calculate merchant fee
const calculateMerchantFee = (paymentResult, totalCharged) => {
    if (paymentResult.paymentType !== "Credit_Card")
        return 0;
    const providerFeeConfig = paymentResult.providerFeeConfig;
    return totalCharged * (providerFeeConfig.fee.value / 100 || 0);
};
// Helper function to check if order is fully paid
const isOrderFullyPaid = (orderLines) => {
    return orderLines.every((ol) => {
        if (!ol.versions)
            return false;
        const latestVersion = (0, orders_1.getLatestOrderLineVersion)(ol.versions);
        if (!latestVersion)
            return false;
        const lineTotal = (0, orders_1.getOrderLineTotal)(ol);
        return latestVersion.amount_paid >= lineTotal;
    });
};
// Helper function to get payment request and invoice
const getPaymentData = async (supabase, transactionId) => {
    const { data: paymentRequest, error: paymentRequestError } = (await supabase
        .from("payment_requests")
        .select(`*`)
        .eq("id", transactionId)
        .single());
    if (paymentRequestError || !paymentRequest) {
        throw new Error(paymentRequestError?.message || "Payment Request not found");
    }
    const { data: invoice, error: invoiceError } = (await supabase
        .from("transactions")
        .select(`*`)
        .eq("id", paymentRequest.invoice_id)
        .single());
    if (invoiceError || !invoice) {
        throw new Error(invoiceError?.message || "Invoice not found");
    }
    return { paymentRequest, invoice };
};
// Helper function to process golf vouchers
const processGolfVouchers = async (orderLines, orderId) => {
    const golfLines = orderLines.filter((ol) => ol.type === "GOLF");
    for (const orderLine of golfLines) {
        const golfEmailObj = {
            language_code: "en",
            template_id: helpers_1.GOLF_VOUCHER,
            order_id: orderId,
            order_line_id: orderLine.id,
        };
        console.log("GOLF VOUCHER - SENDING EMAIL", golfEmailObj);
        await (0, sendEmail_1.sendEmail)(golfEmailObj);
    }
};
// Helper function to update order lines to confirmed paid
const updateOrderLinesToConfirmedPaid = async (supabase, orderLines) => {
    for (const orderLine of orderLines) {
        if (!orderLine.versions)
            continue;
        const latestVersion = (0, orders_1.getLatestOrderLineVersion)(orderLine.versions);
        if (latestVersion) {
            latestVersion.status = helpers_1.OrderLineStatus.CONFIRMED_PAID;
            await (0, orders_1.updateOrderLineVersion)(supabase, latestVersion);
        }
    }
};
const processPaymentRequestUpdate = async (supabase, transaction) => {
    // Early exit if conditions aren't met
    if (!(transaction.old_record.status === "settled" &&
        transaction.old_record.result === null &&
        transaction.record.status === "settled" &&
        transaction.record.result !== null)) {
        return;
    }
    try {
        const paymentResult = transaction.record.result;
        // Get payment request and invoice data
        const { paymentRequest, invoice } = await getPaymentData(supabase, transaction.record.id);
        // Get initial order data
        let order = (await (0, orders_1.getOrderById)(supabase, paymentRequest.order_id));
        let orderLines = getActiveOrderLines(order);
        if (orderLines.length === 0) {
            throw new Error("No order lines to process");
        }
        // Calculate fees
        const totalCharged = paymentRequest.value + paymentResult.feeAmount;
        const merchantFeeCharged = calculateMerchantFee(paymentResult, totalCharged);
        console.log("payment_request", paymentRequest);
        console.log("merchant_fee_charged", merchantFeeCharged);
        // Add order log
        await (0, orders_1.addOrderLog)(supabase, {
            type: "PAYMENT",
            order_id: order.id,
            detail: {
                type: "PAYMENT RECEIVED",
                status: null,
                title: `Invoice ${invoice.reference}`,
                message: `Payment Successfully Processed - ${paymentRequest.currency} ${paymentRequest.value} - Fee: ${merchantFeeCharged}`,
            },
        });
        // Generate receipt
        console.log("######## GENERATE RECEIPT #########");
        try {
            await (0, generateReceipt_1.generateReceipt)(supabase, paymentRequest.order_line_ids, order, paymentResult.paymentType, paymentResult.feeAmount, paymentRequest.value, merchantFeeCharged, paymentRequest.invoice_id);
        }
        catch (error) {
            console.log("ERROR TRYING TO UPDATE ORDER LINE IDs", error);
        }
        console.log("######## RECEIPT GENERATED #########");
        // Re-fetch order data after receipt generation as it may have changed
        console.log("CHECK IS FULLY PAID");
        order = (await (0, orders_1.getOrderById)(supabase, paymentRequest.order_id));
        orderLines = getActiveOrderLines(order);
        // Check if fully paid and process accordingly
        const fullyPaid = isOrderFullyPaid(orderLines);
        console.log("fully_paid", fullyPaid);
        if (fullyPaid) {
            // Update order lines to confirmed paid
            await updateOrderLinesToConfirmedPaid(supabase, orderLines);
            // Update order status
            await (0, orders_1.updateOrderStatus)(supabase, order, "CONFIRMED");
            // Generate supplier bills
            console.log("######## CREATE BILLS #########");
            await (0, orders_1.generateSupplierBills)(supabase, order.id, helpers_1.ADMIN_USER_ID);
            console.log("######## BILLS CREATED #########");
            // Process golf vouchers
            await processGolfVouchers(orderLines, order.id);
        }
    }
    catch (error) {
        console.error("Error processing payment request update:", error);
        throw error;
    }
};
exports.processPaymentRequestUpdate = processPaymentRequestUpdate;
